// 统一导出所有store
export { useUserStore } from '@/modules/user/stores'
export { useProjectStore, ProjectType, ProjectStatus } from '@/modules/project/stores'
export { useTaskStore, TaskType, TaskStatus } from '@/modules/task/stores'
export { useSubtitleEditorStore } from '@/modules/video/stores'
export { useUsageStore } from '@/modules/usage/stores'
export { useAuthStore } from '@/modules/auth/stores'
export { useMessageStore } from '@/modules/message/stores'

// 导出类型
export type { UserState } from '@/modules/user/stores'
export type { 
  Project, 
  ProjectQueryParams, 
  ProjectStats 
} from '@/modules/project/stores'
export type { 
  Task, 
  TaskQueryParams, 
  TaskStats,
} from '@/modules/task/stores'

// 应用状态管理（保留原有的应用级状态）
import { defineStore } from 'pinia'
import { ref } from 'vue'

export const useAppStore = defineStore('app', () => {
  // 侧边栏默认状态：默认为收起（true）
  // 使用版本号来重置所有用户的侧边栏状态为默认收起
  const SIDEBAR_VERSION = 'v4' // 更新版本号以重置所有用户的侧边栏状态
  const sidebarVersionKey = 'sidebar-version'
  const sidebarCollapsedKey = 'sidebar-collapsed'
  
  if (typeof window !== 'undefined') {
    const currentVersion = localStorage.getItem(sidebarVersionKey)
    
    // 如果版本号不匹配，说明需要重置为默认收起状态
    if (currentVersion !== SIDEBAR_VERSION) {
      localStorage.setItem(sidebarCollapsedKey, 'true')
      localStorage.setItem(sidebarVersionKey, SIDEBAR_VERSION)
    }
    
    // 如果没有设置过状态，默认为收起
    if (!localStorage.getItem(sidebarCollapsedKey)) {
      localStorage.setItem(sidebarCollapsedKey, 'true')
    }
  }
  
  // 从localStorage读取，默认值为true（收起）
  const collapsed = ref(typeof window !== 'undefined' 
    ? JSON.parse(localStorage.getItem(sidebarCollapsedKey) || 'true')
    : true
  )
  
  const loading = ref(false)
  const theme = ref<'light' | 'dark'>(
    (localStorage.getItem('app-theme') as 'light' | 'dark') || 'light'
  )
  const locale = ref(localStorage.getItem('app-locale') || 'zh-CN')

  // 全局加载状态
  const globalLoading = ref(false)
  const loadingText = ref('')

  // 页面标题
  const pageTitle = ref('')
  const breadcrumbs = ref<Array<{ title: string; path?: string }>>([])

  // 操作方法
  const toggleSidebar = () => {
    collapsed.value = !collapsed.value
    if (typeof window !== 'undefined') {
      localStorage.setItem(sidebarCollapsedKey, JSON.stringify(collapsed.value))
    }
  }

  const setSidebarCollapsed = (value: boolean) => {
    collapsed.value = value
    if (typeof window !== 'undefined') {
      localStorage.setItem(sidebarCollapsedKey, JSON.stringify(value))
    }
  }

  const setLoading = (value: boolean) => {
    loading.value = value
  }

  const setGlobalLoading = (value: boolean, text = '') => {
    globalLoading.value = value
    loadingText.value = text
  }

  const setTheme = (newTheme: 'light' | 'dark') => {
    theme.value = newTheme
    localStorage.setItem('app-theme', newTheme)
    
    // 更新HTML根元素的class
    document.documentElement.className = newTheme
  }

  const setLocale = (newLocale: string) => {
    locale.value = newLocale
    localStorage.setItem('app-locale', newLocale)
  }

  const setPageTitle = (title: string) => {
    pageTitle.value = title
    document.title = title ? `${title} - CollabLingua` : 'CollabLingua'
  }

  const setBreadcrumbs = (items: Array<{ title: string; path?: string }>) => {
    breadcrumbs.value = items
  }

  // 初始化应用状态
  const initializeApp = () => {
    // 应用主题
    document.documentElement.className = theme.value
    
    // 设置默认标题
    document.title = 'CollabLingua'
  }

  return {
    // 状态
    collapsed,
    loading,
    theme,
    locale,
    globalLoading,
    loadingText,
    pageTitle,
    breadcrumbs,
    
    // 方法
    toggleSidebar,
    setSidebarCollapsed,
    setLoading,
    setGlobalLoading,
    setTheme,
    setLocale,
    setPageTitle,
    setBreadcrumbs,
    initializeApp
  }
})

// 状态持久化工具
export class StorePersistence {
  private static readonly STORAGE_KEY_PREFIX = 'collab-lingua-store-'
  
  // 保存状态到localStorage
  static saveState(storeName: string, state: any) {
    try {
      const key = this.STORAGE_KEY_PREFIX + storeName
      localStorage.setItem(key, JSON.stringify(state))
    } catch (error) {
      console.error(`Failed to save ${storeName} state:`, error)
    }
  }
  
  // 从localStorage恢复状态
  static loadState<T>(storeName: string, defaultState: T): T {
    try {
      const key = this.STORAGE_KEY_PREFIX + storeName
      const saved = localStorage.getItem(key)
      return saved ? JSON.parse(saved) : defaultState
    } catch (error) {
      console.error(`Failed to load ${storeName} state:`, error)
      return defaultState
    }
  }
  
  // 清除指定store的状态
  static clearState(storeName: string) {
    try {
      const key = this.STORAGE_KEY_PREFIX + storeName
      localStorage.removeItem(key)
    } catch (error) {
      console.error(`Failed to clear ${storeName} state:`, error)
    }
  }
  
  // 清除所有store状态
  static clearAllStates() {
    try {
      const keys = Object.keys(localStorage).filter(key => 
        key.startsWith(this.STORAGE_KEY_PREFIX)
      )
      keys.forEach(key => localStorage.removeItem(key))
    } catch (error) {
      console.error('Failed to clear all store states:', error)
    }
  }
}

// 缓存管理工具
export class CacheManager {
  private static caches = new Map<string, Map<string, any>>()
  private static expiries = new Map<string, Map<string, number>>()
  
  // 设置缓存
  static set(namespace: string, key: string, value: any, ttl = 5 * 60 * 1000) {
    if (!this.caches.has(namespace)) {
      this.caches.set(namespace, new Map())
      this.expiries.set(namespace, new Map())
    }
    
    const cache = this.caches.get(namespace)!
    const expiry = this.expiries.get(namespace)!
    
    cache.set(key, value)
    expiry.set(key, Date.now() + ttl)
  }
  
  // 获取缓存
  static get<T>(namespace: string, key: string): T | null {
    const cache = this.caches.get(namespace)
    const expiry = this.expiries.get(namespace)
    
    if (!cache || !expiry) return null
    
    const expiryTime = expiry.get(key)
    if (!expiryTime || Date.now() > expiryTime) {
      // 缓存已过期
      cache.delete(key)
      expiry.delete(key)
      return null
    }
    
    return cache.get(key) || null
  }
  
  // 删除缓存
  static delete(namespace: string, key: string) {
    const cache = this.caches.get(namespace)
    const expiry = this.expiries.get(namespace)
    
    if (cache) cache.delete(key)
    if (expiry) expiry.delete(key)
  }
  
  // 清除命名空间下的所有缓存
  static clear(namespace: string) {
    this.caches.delete(namespace)
    this.expiries.delete(namespace)
  }
  
  // 清除所有缓存
  static clearAll() {
    this.caches.clear()
    this.expiries.clear()
  }
  
  // 清理过期缓存
  static cleanup() {
    const now = Date.now()
    
    this.expiries.forEach((expiry, namespace) => {
      const cache = this.caches.get(namespace)
      if (!cache) return
      
      expiry.forEach((expiryTime, key) => {
        if (now > expiryTime) {
          cache.delete(key)
          expiry.delete(key)
        }
      })
    })
  }
}

// 定期清理过期缓存
setInterval(() => {
  CacheManager.cleanup()
}, 60 * 1000) // 每分钟清理一次

// Store初始化工具
export const initializeStores = () => {
  // 初始化各个store
  const appStore = useAppStore()
  appStore.initializeApp()
  
  // 设置自动保存
  const setupAutoSave = () => {
    // 每30秒自动保存筛选条件和分页状态
    setInterval(() => {
      // 这里可以添加自动保存逻辑
    }, 30 * 1000)
  }
  
  setupAutoSave()
}

// 导出工具类